import { useEffect } from 'react'
import React from 'react'
import {
  Control,
  FieldValues,
  UseFormSetValue,
  UseFormWatch,
  Path,
  Controller
} from 'react-hook-form'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Icon,
  Select,
  Switch,
  Textarea,
  Textfield
} from '@clientbase/clientbase-library'
import {
  Box,
  Checkbox,
  Grid,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material'
import { useFetch } from 'hooks/use-fetch'
import { useInsertAtCursor } from 'hooks/useInsertAtCursor'
import {
  constructorAutoCompleteProvider,
  constructorAutoCompleteServices
} from 'models/ServiceProvider'

import { NatureOption } from 'components/Modal/ModalInvoice/ModalInvoice.utils'
import PopoverNewVariant from 'components/PopoverNewVariant'
import {
  NFSE_DUE_DATE_VARIANTS,
  NFSE_ISSUE_VARIANTS
} from 'components/PopoverNewVariant/variants'

export interface InvoiceProps<T extends FieldValues> {
  control: Control<T, object>
  watch: UseFormWatch<T>
  setValue?: UseFormSetValue<T>
  isEditing?: boolean
  isRecurrence?: boolean
  forceInstallmentScope?: boolean
  /**
   * Renderiza os campos de NF direto (sem o accordion nem o switch de
   * ativar/desativar). Usado no modal de configuração em massa, onde o escopo é
   * apenas configurar. Default `false` preserva o form individual.
   */
  bare?: boolean
}

const Invoice = <T extends FieldValues>({
  control,
  watch,
  setValue,
  isEditing,
  isRecurrence,
  forceInstallmentScope = false,
  bare = false
}: InvoiceProps<T>) => {
  const theme = useTheme()
  const { trackCursor, insertAtCursor } = useInsertAtCursor()

  // Função para verificar se algum meio de pagamento tem parcelamento habilitado (novo formato)
  const hasInstallmentsEnabled = () => {
    const details = watch('payment_type_details' as Path<T>)
    const frequency = watch('frequency' as Path<T>)

    if (!details) return false

    // Se for recorrente (frequency existe e isRecurrence=true) e for semanal (0) ou mensal (1), não permitir parcelamento
    if (
      isRecurrence &&
      frequency !== undefined &&
      (frequency === 0 || frequency === 1)
    ) {
      return false
    }

    const pixInstallments =
      details.pix?.enabled && Number(details.pix?.max_installments || 1) > 1
    const boletoInstallments =
      details.boleto?.enabled &&
      Number(details.boleto?.max_installments || 1) > 1
    const cardInstallments =
      details.credit_card?.enabled &&
      Number(details.credit_card?.max_installments || 1) > 1

    return Boolean(pixInstallments || boletoInstallments || cardInstallments)
  }

  const [fetchNfse, resultNfse] = useFetch({
    method: 'GET',
    shouldRun: false,
    url: '/v1/nfse_issuers/autocomplete'
  })

  const [fetchServices, resultServices] = useFetch({
    method: 'GET',
    shouldRun: false,
    url: `/v1/nfse_issuers`
  })

  useEffect(() => {
    if (
      typeof watch('nfse_issuer_uuid' as Path<T>) === 'string' &&
      watch('nfse_issuer_uuid' as Path<T>) !== ''
    ) {
      fetchServices({
        paramUrl: `${watch('nfse_issuer_uuid' as Path<T>)}/services`
      })
    }
  }, [watch('nfse_issuer_uuid' as Path<T>)])

  // Auto-select service item when only one is available
  useEffect(() => {
    if (resultServices?.data && setValue) {
      const services = constructorAutoCompleteServices(resultServices.data)
      const currentServiceCode = watch('service_list_code' as Path<T>)

      // Verifica se o valor atual ainda existe na lista de serviços
      const currentServiceExists = services.some(
        (service) => service.uuid === currentServiceCode
      )

      // Se o valor atual não existe mais na lista (prestador foi alterado), limpa o campo
      if (currentServiceCode && !currentServiceExists) {
        setValue('service_list_code' as Path<T>, '' as any)

        // Se após limpar, há apenas um serviço disponível, seleciona automaticamente
        if (services.length === 1) {
          setValue('service_list_code' as Path<T>, services[0].uuid as any)
        }
      }
      // Se há apenas um serviço e não há valor selecionado, seleciona automaticamente
      else if (services.length === 1 && !currentServiceCode) {
        setValue('service_list_code' as Path<T>, services[0].uuid as any)
      }
    }
  }, [resultServices])

  useEffect(() => {
    if (watch('interestInvoice' as Path<T>)) {
      fetchNfse({})
    }
  }, [watch('interestInvoice' as Path<T>)])

  // Auto-select provider when only one is available
  useEffect(() => {
    if (resultNfse?.data) {
      const providers = constructorAutoCompleteProvider(resultNfse.data)
      const currentProviderUuid = watch('nfse_issuer_uuid' as Path<T>)

      // Verifica se o valor atual ainda existe na lista de prestadores
      const currentProviderExists = providers.some(
        (provider) => provider.uuid === currentProviderUuid
      )

      // Se há apenas um prestador e não há valor selecionado, seleciona automaticamente
      if (providers.length === 1 && !currentProviderUuid) {
        !!setValue &&
          setValue('nfse_issuer_uuid' as Path<T>, providers[0].uuid as any)
      }
      // Se o valor atual não existe mais na lista, limpa o campo
      else if (currentProviderUuid && !currentProviderExists) {
        !!setValue && setValue('nfse_issuer_uuid' as Path<T>, '' as any)
      }
    }
  }, [resultNfse])

  const fieldsContent = (
    <Grid container={true} spacing={2}>
      <Grid item={true} xs={12} sm={12} lg={4}>
        <Controller
          name={'nfse_issuer_uuid' as Path<T>}
          control={control}
          render={({
            field: { onChange, value, ref },
            fieldState: { error }
          }) => (
            <Select
              inputRef={ref}
              label="Prestador de serviço"
              helperText={error?.message}
              error={!!error?.message}
              fullWidth
              onChange={onChange}
              value={value}
              displayEmpty
            >
              <MenuItem disabled value="" color={theme.palette.neutral[600]}>
                <Typography color={theme.palette.neutral[600]} fontWeight={300}>
                  Selecione um prestador de serviço
                </Typography>
              </MenuItem>
              {resultNfse &&
                constructorAutoCompleteProvider(resultNfse.data).map(
                  ({ legal_name, uuid }) => (
                    <MenuItem key={uuid} value={uuid}>
                      {legal_name}
                    </MenuItem>
                  )
                )}
            </Select>
          )}
        />
      </Grid>
      <Grid item={true} xs={12} sm={12} md={12} lg={4}>
        <Controller
          name={'nature_operation' as Path<T>}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              helperText={error?.message}
              error={!!error?.message}
              onChange={onChange}
              label="Natureza da operação"
              value={value}
              displayEmpty
              fullWidth
            >
              <MenuItem disabled value="" color={theme.palette.neutral[600]}>
                <Typography color={theme.palette.neutral[600]} fontWeight={300}>
                  Selecione uma natureza da operação
                </Typography>
              </MenuItem>
              {NatureOption.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </Grid>
      <Grid item={true} xs={12} sm={12} lg={4}>
        <Controller
          name={'service_list_code' as Path<T>}
          control={control}
          render={({
            field: { onChange, value, ref },
            fieldState: { error }
          }) => (
            <Select
              inputRef={ref}
              label="Item de serviço"
              helperText={error?.message}
              error={!!error?.message}
              fullWidth
              onChange={onChange}
              value={value}
              disabled={!watch('nfse_issuer_uuid' as Path<T>)}
              displayEmpty
            >
              <MenuItem disabled value="" color={theme.palette.neutral[600]}>
                <Typography color={theme.palette.neutral[600]} fontWeight={300}>
                  Selecione um item de serviço
                </Typography>
              </MenuItem>
              {resultServices &&
                constructorAutoCompleteServices(resultServices.data).map(
                  ({ description, uuid }) => (
                    <MenuItem key={uuid} value={uuid}>
                      {description}
                    </MenuItem>
                  )
                )}
            </Select>
          )}
        />
      </Grid>
      <Grid item={true} xs={12}>
        <Controller
          name={'descriptionInvoice' as Path<T>}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Box>
              <Textarea
                label="Descreva sua nota"
                style={{
                  minHeight: '100px',
                  resize: 'vertical',
                  maxHeight: '50vh'
                }}
                placeholder="Digite aqui a descrição"
                value={value}
                onChange={onChange}
                onSelect={trackCursor}
                helperText={error?.message}
                error={!!error?.message}
                disabled={watch('copy_description' as Path<T>) as boolean}
              />
            </Box>
          )}
        />

        {/*
          Sem setValue (modal de config em massa, `bare`) não há como inserir o token.
          Com copy_description a descrição vem da cobrança e o campo fica disabled.
          Variáveis de vencimento saem de cena no parcelamento: a nota é consolidada
          por ciclo e as parcelas vencem em meses diferentes (o backend rejeita).
        */}
        {setValue && !watch('copy_description' as Path<T>) && (
          <PopoverNewVariant
            variants={
              hasInstallmentsEnabled() || forceInstallmentScope
                ? NFSE_ISSUE_VARIANTS
                : [...NFSE_ISSUE_VARIANTS, ...NFSE_DUE_DATE_VARIANTS]
            }
            description={watch('descriptionInvoice' as Path<T>) as string}
            setDescription={(message) =>
              setValue(
                'descriptionInvoice' as Path<T>,
                message as Parameters<typeof setValue>[1]
              )
            }
            insertAtCursor={insertAtCursor}
            noChars
          />
        )}

        <Controller
          control={control}
          name={'copy_description' as Path<T>}
          render={({ field }) => {
            return (
              <Box display="flex" alignItems="center" gap={1} mt={0.8}>
                <Checkbox
                  inputRef={field.ref}
                  {...field}
                  checked={field.value || false}
                  sx={{ p: 0 }}
                  id="copy_description_checkbox"
                />

                <Typography
                  component="label"
                  htmlFor="copy_description_checkbox"
                  variant="smLight"
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Copiar descrição da cobrança
                </Typography>

                <Tooltip
                  arrow
                  placement="right"
                  title={
                    <Typography variant="xsLight" color="white">
                      A descrição da nota fiscal será igual a descrição da
                      fatura na hora da emissão.
                    </Typography>
                  }
                >
                  <Typography display="flex" alignItems="center">
                    <Icon
                      icon="info"
                      sx={{ fontSize: '1.2rem', color: '#BEC0BE' }}
                    />
                  </Typography>
                </Tooltip>
              </Box>
            )
          }}
        />

        {/* Campo de valor total ou valor da parcela */}
        {(hasInstallmentsEnabled() || forceInstallmentScope) && (
          <Box sx={{ mt: 2 }}>
            <Controller
              control={control}
              name={'nfse_installment_scope' as Path<T>}
              render={({
                field: { onChange, value, ref },
                fieldState: { error }
              }) => (
                <Select
                  inputRef={ref}
                  label="Valor total ou valor da parcela"
                  helperText={error?.message || ' '}
                  error={!!error?.message}
                  fullWidth
                  onChange={onChange}
                  value={value || 'per_cycle'}
                  displayEmpty
                >
                  <MenuItem value="per_cycle">
                    <Stack
                      direction="row"
                      alignItems="center"
                      mt={0.2}
                      gap={0.5}
                    >
                      <Typography>Valor total do parcelamento</Typography>
                      <Typography color={theme.palette.neutral[400]}>
                        {isRecurrence
                          ? '(uma única vez por ciclo)'
                          : '(uma única nota)'}
                      </Typography>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="per_installment">
                    <Stack
                      direction="row"
                      alignItems="center"
                      mt={0.2}
                      gap={0.5}
                    >
                      <Typography>Valor da parcela</Typography>
                      <Typography color={theme.palette.neutral[400]}>
                        (por mês)
                      </Typography>
                    </Stack>
                  </MenuItem>
                </Select>
              )}
            />
          </Box>
        )}
      </Grid>
      <Grid
        item={true}
        xs={12}
        sm={12}
        lg={
          watch('amount_type' as Path<T>)?.toString() !== 'amount_custom'
            ? 4
            : watch('issue_when' as Path<T>)?.toString() === 'on_custom_issued'
            ? 4
            : 12
        }
      >
        <Controller
          name={'issue_when' as Path<T>}
          control={control}
          render={({
            field: { onChange, value, ref },
            fieldState: { error }
          }) => (
            <Select
              inputRef={ref}
              label={
                bare
                  ? 'Quando emitir a NF?'
                  : 'Quando deseja que a NF seja emitida?'
              }
              helperText={error?.message}
              error={!!error?.message}
              fullWidth
              onChange={onChange}
              value={value}
              defaultValue={'on_billing_issued'}
              displayEmpty
            >
              <MenuItem value={'on_billing_issued'}>
                Quando a fatura abrir para pagamento
              </MenuItem>
              <MenuItem value={'on_billing_paid'}>
                Quando o pagamento for efetuado
              </MenuItem>
              <MenuItem value={'on_custom_issued'}>Customizado</MenuItem>
            </Select>
          )}
        />
      </Grid>
      <Grid
        item={true}
        xs={12}
        sm={12}
        md={12}
        lg={watch('amount_type' as Path<T>)?.toString() ? 4 : 8}
      >
        <Controller
          name={'amount_type' as Path<T>}
          control={control}
          render={({
            field: { onChange, value, ref },
            fieldState: { error }
          }) => (
            <Select
              inputRef={ref}
              label="Qual o valor da nota?"
              helperText={error?.message}
              error={!!error?.message}
              fullWidth
              onChange={onChange}
              value={value}
              displayEmpty
            >
              <MenuItem
                disabled={
                  watch('issue_when' as Path<T>)?.toString() !==
                    'on_billing_paid' || isEditing
                }
                value={'amount_paid'}
              >
                Valor pago
              </MenuItem>
              <MenuItem value={'amount_issued'}> Valor original</MenuItem>
              <MenuItem value={'amount_custom'}> Valor personalizado</MenuItem>
            </Select>
          )}
        />
      </Grid>
      {watch('amount_type' as Path<T>)?.toString() === 'amount_custom' && (
        <Grid item={true} xs={12} sm={12} md={12} lg={4}>
          <Controller
            name={'amountCustom' as Path<T>}
            control={control}
            render={({
              field: { onChange, value, ref },
              fieldState: { error }
            }) => (
              <Textfield
                inputRef={ref}
                style={{
                  marginTop: '21px'
                }}
                customStartAdornment={
                  <Typography mr="6px" variant="smLight">
                    R$
                  </Typography>
                }
                placeholder="Digite o valor"
                startAdornment={true}
                mask="money"
                fullWidth
                onChange={onChange}
                value={value}
                helperText={
                  watch('amount_type' as Path<T>) === 'amount_custom' &&
                  error?.message
                }
                error={!!error?.message}
                disabled={watch('amount_type' as Path<T>) !== 'amount_custom'}
              />
            )}
          />
        </Grid>
      )}
      <Grid item={true} xs={12} sm={12} md={12} lg={4}>
        <Controller
          name={'iss_retention' as Path<T>}
          control={control}
          render={({
            field: { onChange, value, ref },
            fieldState: { error }
          }) => (
            <Select
              inputRef={ref}
              label="ISS retido na fonte"
              helperText={error?.message}
              error={!!error?.message}
              fullWidth
              onChange={onChange}
              value={value}
              displayEmpty
            >
              <MenuItem value={'yes'}>Sim</MenuItem>
              <MenuItem value={'no'}>Não</MenuItem>
            </Select>
          )}
        />
      </Grid>
      {watch('issue_when' as Path<T>) === 'on_custom_issued' && (
        <Grid item={true} xs={12} sm={12} lg={4}>
          <Controller
            name={'issue_mode' as Path<T>}
            control={control}
            render={({
              field: { onChange, value, ref },
              fieldState: { error }
            }) => (
              <Select
                inputRef={ref}
                label="Tipo de Configuração"
                helperText={error?.message}
                error={!!error?.message}
                fullWidth
                onChange={onChange}
                value={value || 'before_due_date'}
                displayEmpty
              >
                <MenuItem value={'before_due_date'}>
                  Antes do vencimento
                </MenuItem>
                <MenuItem value={'after_due_date'}>
                  Depois do vencimento
                </MenuItem>
                <MenuItem value={'specific_day'}>
                  Dia específico do mês
                </MenuItem>
              </Select>
            )}
          />
        </Grid>
      )}
      {watch('issue_when' as Path<T>) === 'on_custom_issued' &&
        (watch('issue_mode' as Path<T>) === 'before_due_date' ||
          watch('issue_mode' as Path<T>) === 'after_due_date') && (
          <Grid item={true} xs={12} sm={12} lg={4}>
            <Controller
              name={'days_offset' as Path<T>}
              control={control}
              render={({
                field: { onChange, value, ref },
                fieldState: { error }
              }) => (
                <Textfield
                  inputRef={ref}
                  label={
                    watch('issue_mode' as Path<T>) === 'before_due_date'
                      ? 'Quantos dias antes do vencimento?'
                      : 'Quantos dias depois do vencimento?'
                  }
                  placeholder="Digite o número de dias"
                  fullWidth
                  onChange={(e) => {
                    const onlyNumbers = e.target.value.replace(/[^0-9]/g, '')
                    onChange(onlyNumbers)
                  }}
                  value={value || ''}
                  helperText={error?.message}
                  error={!!error?.message}
                />
              )}
            />
          </Grid>
        )}
      {watch('issue_when' as Path<T>) === 'on_custom_issued' &&
        watch('issue_mode' as Path<T>) === 'specific_day' && (
          <>
            <Grid item={true} xs={12} sm={12} lg={4}>
              <Controller
                name={'specific_day' as Path<T>}
                control={control}
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error }
                }) => (
                  <Box position="relative">
                    <Select
                      inputRef={ref}
                      label={
                        <Box display="flex" alignItems="center" gap="5px">
                          <Typography>Dia do mês para emissão</Typography>
                          <Tooltip
                            arrow
                            placement="right"
                            title={
                              <Typography variant="xsLight" color="white">
                                Quando o dia escolhido não existir no mês (ex:
                                31 de fevereiro), a nota será emitida no último
                                dia do mês.
                              </Typography>
                            }
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              sx={{
                                cursor: 'pointer',
                                marginTop: '-2px'
                              }}
                            >
                              <Icon
                                icon="info"
                                sx={{
                                  fontSize: '1.2rem',
                                  color: '#BEC0BE'
                                }}
                              />
                            </Box>
                          </Tooltip>
                        </Box>
                      }
                      helperText={
                        value > 28 && !error?.message
                          ? 'Atenção: Este dia não existe em todos os meses'
                          : error?.message
                      }
                      error={!!error?.message}
                      fullWidth
                      onChange={onChange}
                      value={value || ''}
                      displayEmpty
                    >
                      <MenuItem disabled value="">
                        <Typography
                          color={theme.palette.neutral[600]}
                          fontWeight={300}
                        >
                          Selecione o dia
                        </Typography>
                      </MenuItem>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(
                        (day) => (
                          <MenuItem key={day} value={day}>
                            {day}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </Box>
                )}
              />
            </Grid>

            <Grid item={true} xs={12} sm={12} lg={4}>
              <Controller
                name={'specific_month_offset' as Path<T>}
                control={control}
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error }
                }) => (
                  <Select
                    inputRef={ref}
                    label="Mês de emissão"
                    helperText={error?.message}
                    error={!!error?.message}
                    fullWidth
                    onChange={onChange}
                    value={value || ''}
                    displayEmpty
                  >
                    <MenuItem disabled value="">
                      <Typography
                        color={theme.palette.neutral[600]}
                        fontWeight={300}
                      >
                        Selecione quando emitir
                      </Typography>
                    </MenuItem>
                    <MenuItem value={'-2'}>
                      Dois meses antes do vencimento
                    </MenuItem>
                    <MenuItem value={'-1'}>Mês anterior ao vencimento</MenuItem>
                    <MenuItem value={'0'}>Mês do vencimento</MenuItem>
                    <MenuItem value={'1'}>Mês seguinte do vencimento</MenuItem>
                    <MenuItem value={'2'}>
                      Dois meses depois do vencimento
                    </MenuItem>
                  </Select>
                )}
              />
            </Grid>
          </>
        )}
    </Grid>
  )

  if (bare) {
    return fieldsContent
  }

  return (
    <Controller
      control={control}
      name={'interestInvoice' as Path<T>}
      render={({ field }) => (
        <Accordion
          expanded={!!field.value}
          onChange={(_, expanded) =>
            !!setValue &&
            setValue('interestInvoice' as Path<T>, expanded as any)
          }
          sx={{
            border: '1px solid #E8EBE8',
            borderLeft: '3px solid',
            borderLeftColor: 'primary.main',
            borderRadius: '8px !important',
            backgroundColor: '#FAFBFA',
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '&.Mui-expanded': {
              margin: 0,
              backgroundColor: '#fff'
            }
          }}
        >
          <AccordionSummary
            hasSwitch
            expandIcon={<Switch checked={field.value} {...field} />}
          >
            <Typography variant="smBold">Nota Fiscal</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>{fieldsContent}</AccordionDetails>
        </Accordion>
      )}
    />
  )
}

export default Invoice
